import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Gift, Check, Loader2, Copy, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export default function GiftSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const giftId = searchParams.get("gift_id");
  const [gift, setGift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!giftId || !token) { setLoading(false); return; }
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/gift/status/${giftId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setGift(data);
        if (data.payment_status === "paid") return true;
        return false;
      } catch { return false; }
    };
    const startPolling = async () => {
      const done = await poll();
      if (done) { setLoading(false); return; }
      let count = 0;
      const interval = setInterval(async () => {
        count++;
        const done = await poll();
        if (done || count >= 10) { clearInterval(interval); setLoading(false); }
      }, 2000);
    };
    startPolling();
  }, [giftId, token]);

  const copyCode = async () => {
    if (!gift?.gift_code) return;
    try {
      await navigator.clipboard.writeText(gift.gift_code);
      setCopied(true);
      toast.success("Gift code copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Copy failed"); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-3">
          <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto" />
          <p className="text-sm text-muted-foreground">Confirming payment...</p>
        </div>
      </div>
    );
  }

  if (!gift) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="text-center space-y-3">
          <p className="text-muted-foreground">Gift not found</p>
          <Button onClick={() => navigate("/gift")} className="rounded-full">Try Again</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md text-center space-y-6 animate-in fade-in zoom-in-95">
        <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center">
          <Gift className="w-10 h-10 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-heading font-bold mb-1">Gift Purchased!</h1>
          <p className="text-sm text-muted-foreground">{gift.tier_name} — {gift.duration_label}</p>
        </div>
        <div className="bg-card border border-border/50 rounded-2xl p-5 space-y-3">
          <p className="text-[11px] text-muted-foreground">Gift Code</p>
          <div className="flex items-center justify-center gap-2">
            <code data-testid="gift-code" className="text-2xl font-mono font-bold tracking-wider text-primary">{gift.gift_code}</code>
            <button onClick={copyCode} className={cn("p-2 rounded-lg transition-all", copied ? "bg-emerald-100 text-emerald-600" : "bg-muted/50 text-muted-foreground")}>
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
          <p className="text-[10px] text-muted-foreground">Send this code to {gift.recipient_email}</p>
          <p className="text-[10px] text-muted-foreground/60">Expires {new Date(gift.expires_at).toLocaleDateString()}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => navigate("/gift")} variant="outline" className="flex-1 rounded-full">Gift Another</Button>
          <Button onClick={() => navigate("/chat")} className="flex-1 rounded-full bg-primary text-primary-foreground gap-1">Chat <ArrowRight className="w-4 h-4" /></Button>
        </div>
      </div>
    </div>
  );
}
